import {
  Component,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, combineLatest, from, fromEvent, merge, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { WorkService } from '../services';
import { DiallerService, SIPService } from '../../dialler/services';
import { TeleworkerState } from '@app/core/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SubSink } from 'subsink';
import {
  SpeeddialComponent,
  TransferComponent,
} from '@app/transfer/components';
import {
  LoggerService,
  shareReplayRefCount,
  Unity4Claim,
} from '@adreno-common';
import { HubConnectionState } from '@microsoft/signalr';
import { ToastrService } from 'ngx-toastr';
@Component({
  selector: 'app-dial-pad',
  templateUrl: './dial-pad.component.html',
  styles: [
    '.btn-sm { line-height: 14px; border-radius: 4px; }',
    '.form-control-sm { font-size: 12px; height: 25px; }',
    'small { font-size: 12px; font-weight: 700; }',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialPadComponent implements OnInit, OnDestroy {
  private answerBtn$!: Observable<ButtonState>;
  private cancelBtn$!: Observable<ButtonState>;
  private dialBtn$!: Observable<ButtonState>;
  private dtmfMode$!: Observable<boolean>;
  private dtmfTones!: FormControl;
  private hangupBtn$!: Observable<ButtonState>;
  private holdBtn$!: Observable<ButtonState>;
  private numpadBtns$!: Observable<ButtonState>;
  private phoneNumber!: FormControl;
  private phoneNumberInp$!: Observable<ButtonState>;
  private remoteAudio!: HTMLAudioElement;
  private speedDialBtn$!: Observable<ButtonState>;
  private subs = new SubSink();
  private transferBtn$!: Observable<ButtonState>;
  private unavailableBtn$!: Observable<ButtonState>;
  private unholdBtn$!: Observable<ButtonState>;

  Unity4Claim = Unity4Claim;
  formGroup!: FormGroup;

  viewModel$!: Observable<ViewModel>;

  constructor(
    private fb: FormBuilder,
    private workSvc: WorkService,
    private diallerSvc: DiallerService,
    private modalService: NgbModal,
    private sipSvc: SIPService,
    private logger: LoggerService,
    private toastr: ToastrService
  ) {}

  private callSpeakerDevice() {
    return this.sipSvc.listDevices('audiooutput').pipe(
      map((devices) => {
        if (devices.length === 0) {
          this.logger.error(
            'DialPadComponent',
            'callSpeakerDevice()',
            'No audio output devices found'
          )();
          this.toastr.error('No audio output devices found', 'VOIP Error', {
            disableTimeOut: true,
            closeButton: true,
          });
          return null;
        }
        return devices[0];
      })
    );
  }

  private initButtonStates() {
    this.answerBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.diallerSvc.onVoiceMailCall$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([twState, onVoicemailCall, connected]) => {
        switch (twState) {
          case TeleworkerState.CallDialling:
          case TeleworkerState.InboundConnected:
          case TeleworkerState.OutboundConnected:
          case TeleworkerState.HoldIB:
          case TeleworkerState.HoldOB:
          case TeleworkerState.WrapIB:
          case TeleworkerState.WrapOB:
          case TeleworkerState.TransferIB:
          case TeleworkerState.TransferOB:
          case TeleworkerState.CallDisconnected:
            return {
              visible: false,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
          case TeleworkerState.InboundWaiting:
          case TeleworkerState.Ringing:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          default:
            return {
              visible: !onVoicemailCall,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
        }
      })
    );

    this.cancelBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([twState, connected]) => {
        switch (twState) {
          case TeleworkerState.TransferIB:
          case TeleworkerState.TransferOB:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          default:
            return {
              visible: false,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
        }
      })
    );

    this.dialBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.workSvc.hasIBScheduled$,
      this.diallerSvc.onVoiceMailCall$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([twState, hasIBScheduled, onVoicemailCall, connected]) => {
        switch (twState) {
          case TeleworkerState.Work:
          case TeleworkerState.Unavailable:
          case TeleworkerState.UnavailableIB:
          case TeleworkerState.UnavailableOB:
          case TeleworkerState.CallDisconnected:
          case TeleworkerState.WrapIB:
          case TeleworkerState.WrapOB:
            return {
              visible: hasIBScheduled && !onVoicemailCall,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          case TeleworkerState.AvailableIB:
          case TeleworkerState.DiallerLogin:
          case TeleworkerState.DiallerLoggedIn:
          case TeleworkerState.SIPNailIn:
          case TeleworkerState.AwaitingNailIn:
          case TeleworkerState.CallDialling:
          case TeleworkerState.InboundConnected:
          case TeleworkerState.OutboundConnected:
          case TeleworkerState.HoldIB:
          case TeleworkerState.HoldOB:
          case TeleworkerState.TransferIB:
          case TeleworkerState.TransferOB:
          case TeleworkerState.Standby:
            return {
              visible: hasIBScheduled && !onVoicemailCall,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };

          default:
            return {
              visible: false,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
        }
      })
    );

    this.hangupBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.diallerSvc.onVoiceMailCall$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([twState, onVoicemailCall, connected]) => {
        switch (twState) {
          case TeleworkerState.CallDialling:
          case TeleworkerState.OutboundConnected:
          case TeleworkerState.InboundConnected:
          case TeleworkerState.TransferIB:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          case TeleworkerState.TransferOB:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          case TeleworkerState.HoldIB:
          case TeleworkerState.HoldOB:
          case TeleworkerState.WrapIB:
          case TeleworkerState.WrapOB:
          case TeleworkerState.CallDisconnected:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
          default:
            return {
              visible: onVoicemailCall,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
        }
      })
    );

    this.holdBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([twState, connected]) => {
        switch (twState) {
          case TeleworkerState.CallDisconnected:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
          case TeleworkerState.InboundConnected:
          case TeleworkerState.OutboundConnected:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          case TeleworkerState.HoldIB:
          case TeleworkerState.HoldOB:
            return {
              visible: false,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
          default:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
        }
      })
    );

    this.numpadBtns$ = combineLatest([
      this.diallerSvc.state$,
      this.workSvc.hasIBScheduled$,
      this.sipSvc.voip$,
      this.diallerSvc.connectedState$,
      this.dtmfMode$,
    ]).pipe(
      map(([twState, hasIBScheduled, voip, connected]) => {
        const visible = true;
        const hoverText =
          connected === HubConnectionState.Connected
            ? ''
            : 'You are currently not connected';
        const enabled = connected === HubConnectionState.Connected;
        switch (twState) {
          case TeleworkerState.Work:
          case TeleworkerState.WrapIB:
          case TeleworkerState.WrapOB:
          case TeleworkerState.CallDisconnected:
            return {
              visible,
              hoverText,
              enabled: enabled && hasIBScheduled,
            };
          case TeleworkerState.InboundConnected:
          case TeleworkerState.OutboundConnected:
          case TeleworkerState.HoldIB:
          case TeleworkerState.HoldOB:
            return {
              visible,
              hoverText,
              enabled: enabled && voip,
            };
          default:
            return {
              visible,
              hoverText,
              enabled,
            };
        }
      })
    );

    this.phoneNumberInp$ = combineLatest([
      this.diallerSvc.state$,
      this.workSvc.hasIBScheduled$,
      this.diallerSvc.onVoiceMailCall$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([twState, hasIBScheduled, onVoicemailCall, connected]) => {
        const visible = !onVoicemailCall && hasIBScheduled;
        const enabled = connected === HubConnectionState.Connected;
        const hoverText =
          connected === HubConnectionState.Connected
            ? ''
            : 'You are currently not connected';
        switch (twState) {
          case TeleworkerState.Standby:
            return {
              visible,
              hoverText,
              enabled: false,
            };
          default:
            return {
              visible,
              hoverText,
              enabled,
            };
        }
      })
    );

    this.speedDialBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.workSvc.hasIBScheduled$,
      this.diallerSvc.onVoiceMailCall$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      filter(
        ([twState, hasIBScheduled, connected]) =>
          twState !== TeleworkerState.CallDisconnected
      ),
      map(([twState, hasIBScheduled, onVoicemailCall, connected]) => {
        switch (twState) {
          case TeleworkerState.Unavailable:
          case TeleworkerState.UnavailableIB:
          case TeleworkerState.UnavailableOB:
            return {
              visible: hasIBScheduled && !onVoicemailCall,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          case TeleworkerState.WrapIB:
          case TeleworkerState.WrapOB:
            return {
              visible: hasIBScheduled,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: hasIBScheduled,
            };
          default:
            return {
              visible: false,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
        }
      })
    );

    this.transferBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.workSvc.hasIBScheduled$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([twState, hasIBScheduled, connected]) => {
        switch (twState) {
          case TeleworkerState.Unavailable:
          case TeleworkerState.UnavailableIB:
          case TeleworkerState.UnavailableOB:
            return {
              visible: !hasIBScheduled,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
          case TeleworkerState.WrapIB:
          case TeleworkerState.WrapOB:
            return {
              visible: !hasIBScheduled,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: !hasIBScheduled,
            };
          case TeleworkerState.CallDisconnected:
            return {
              visible: !hasIBScheduled,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
          case TeleworkerState.TransferIB:
            return {
              visible: false,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
          case TeleworkerState.InboundConnected:
          case TeleworkerState.OutboundConnected:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          default:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
        }
      })
    );

    this.unavailableBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.diallerSvc.onVoiceMailCall$,
      this.workSvc.obLaunched$,
      this.workSvc.campaignScriptOpen$,
      this.workSvc.campaignId$,
      combineLatest([
        this.workSvc.hasIBScheduled$,
        this.diallerSvc.connectedState$,
      ]),
    ]).pipe(
      map(
        ([
          twState,
          onVoicemailCall,
          obLaunched,
          campaignScriptOpen,
          campaignId,
          [hasIBScheduled, connected],
        ]) => {
          switch (twState) {
            case TeleworkerState.AvailableIB:
              return {
                visible: true,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled:
                  connected === HubConnectionState.Connected &&
                  !(
                    campaignId !== undefined &&
                    campaignId !== 0 &&
                    !obLaunched &&
                    campaignScriptOpen
                  ) &&
                  !onVoicemailCall,
              };
            case TeleworkerState.CallDisconnected:
              return {
                visible: hasIBScheduled,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled:
                  connected === HubConnectionState.Connected &&
                  !campaignScriptOpen &&
                  !onVoicemailCall &&
                  !obLaunched,
              };
            case TeleworkerState.CallDialling:
            case TeleworkerState.OutboundConnected:
            case TeleworkerState.WrapOB:
            case TeleworkerState.HoldOB:
            case TeleworkerState.InboundConnected:
              return {
                visible: hasIBScheduled,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled:
                  connected === HubConnectionState.Connected &&
                  !obLaunched &&
                  !campaignScriptOpen &&
                  !onVoicemailCall &&
                  !hasIBScheduled,
              };
            case TeleworkerState.Standby:
              return {
                visible: hasIBScheduled,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled: connected === HubConnectionState.Connected,
              };
            case TeleworkerState.Voicemail:
              return {
                visible: connected === HubConnectionState.Connected,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled: false,
              };
            default:
              return {
                visible: hasIBScheduled,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled:
                  connected === HubConnectionState.Connected &&
                  !campaignScriptOpen &&
                  !onVoicemailCall,
              };
          }
        }
      )
    );

    this.unholdBtn$ = this.diallerSvc.state$.pipe(
      withLatestFrom(this.diallerSvc.connectedState$),
      map(([twState, connected]) => {
        switch (twState) {
          case TeleworkerState.HoldIB:
          case TeleworkerState.HoldOB:
            return {
              visible: true,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: connected === HubConnectionState.Connected,
            };
          default:
            return {
              visible: false,
              hoverText:
                connected === HubConnectionState.Connected
                  ? ''
                  : 'You are currently not connected',
              enabled: false,
            };
        }
      })
    );
  }

  private initFormGroup() {
    this.formGroup = this.fb.group({ phoneNumber: '', dtmfTones: '' });
    this.phoneNumber = this.formGroup.get('phoneNumber') as FormControl;
    this.dtmfTones = this.formGroup.get('dtmfTones') as FormControl;

    this.subs.sink = this.phoneNumber.valueChanges.subscribe((val: string) => {
      if (val) {
        this.phoneNumber.setValue(val.replace(/[^0-9]/g, ''), {
          emitEvent: false,
        });
      }
    });

    this.subs.sink = this.dtmfTones.valueChanges.subscribe((val: string) => {
      if (val) {
        this.dtmfTones.setValue(val.replace(/[^0-9*#]/g, ''), {
          emitEvent: false,
        });
      }
    });

    this.subs.sink = this.dtmfMode$
      .pipe(
        distinctUntilChanged(),
        filter((dtmfMode) => !!dtmfMode)
      )
      .subscribe(() => this.dtmfTones.setValue(''));
  }

  private initRemoteAudio() {
    this.remoteAudio = new Audio();
    this.remoteAudio.crossOrigin = 'anonymous';

    this.subs.sink = this.sipSvc.speakerStream$
      .pipe(
        filter((stream) => !!stream),
        map((stream) => stream!),
        switchMap((stream) =>
          this.callSpeakerDevice().pipe(
            map((audioDevice) => [stream, audioDevice] as const)
          )
        ),
        switchMap(([stream, audioDevice]) => {
          this.logger.info('DialPadComponent', 'remoteAudio.setSinkId', {
            stream: {
              label: stream?.getTracks()[0]?.label,
              active: stream?.active,
            },
            audioDevice: {
              deviceId: audioDevice?.deviceId,
              kind: audioDevice?.kind,
              label: audioDevice?.label,
            },
          })();
          if (audioDevice) {
            return from(
              (this.remoteAudio as any).setSinkId(audioDevice.deviceId)
            ).pipe(map(() => stream));
          } else {
            return of(stream);
          }
        }),
        switchMap((stream) => {
          this.remoteAudio.pause();
          this.remoteAudio.srcObject = stream;
          this.remoteAudio.load();
          return fromEvent(this.remoteAudio, 'canplay');
        }),
        switchMap(() => {
          this.logger.debug('DialPadComponent', 'remoteAudio.play()')();
          return from(this.remoteAudio.play());
        })
      )
      .subscribe({
        next: () => {
          this.logger.debug('DialPadComponent', 'remoteAudio.play() ended')();
        },
        error: (err) => {
          this.logger.error('DialPadComponent', 'remoteAudio.play()', err)();
        },
      });
  }

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      this.hangupBtn$.pipe(map((btnState) => ({ hangUp: btnState }))),
      this.dialBtn$.pipe(map((btnState) => ({ dial: btnState }))),
      this.numpadBtns$.pipe(map((btnState) => ({ numpad: btnState }))),
      this.answerBtn$.pipe(map((btnState) => ({ answer: btnState }))),
      this.cancelBtn$.pipe(map((btnState) => ({ cancel: btnState }))),
      this.holdBtn$.pipe(map((btnState) => ({ hold: btnState }))),
      this.unholdBtn$.pipe(map((btnState) => ({ unhold: btnState }))),
      this.sipSvc.status$.pipe(map((status) => ({ sipStatus: status }))),
      this.phoneNumberInp$.pipe(map((btnState) => ({ phoneNumber: btnState }))),
      this.speedDialBtn$.pipe(map((btnState) => ({ speedDial: btnState }))),
      this.transferBtn$.pipe(map((btnState) => ({ transfer: btnState }))),
      this.unavailableBtn$.pipe(map((btnState) => ({ unavailable: btnState }))),
      this.dtmfMode$.pipe(map((dtmfMode) => ({ dtmfMode })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  answer() {
    this.logger.info('DialPadComponent', 'answer()')();
    this.diallerSvc.inboundConnect();
  }

  available() {
    this.logger.info('DialPadComponent', 'available()')();
    this.diallerSvc.availableib();
  }

  cancel() {
    this.logger.info('DialPadComponent', 'cancel()')();
    this.diallerSvc.onVoiceMailCall$
      .pipe(take(1))
      .subscribe((onVoiceMailCall) => {
        if (
          this.diallerSvc.xferStopRecording &&
          !this.diallerSvc.agent3WayPending
        ) {
          // we have stopped the recording and it wasn't a 3-way (which stops upon hangup). so restart it (or start a new one I guess)
          this.diallerSvc.startRecording();
        }
        this.diallerSvc.cancelTransfer(onVoiceMailCall);
      });
  }

  clear() {
    this.logger.info('DialPadComponent', 'clear()')();
    this.phoneNumber?.setValue('');
  }

  dial() {
    this.logger.info('DialPadComponent', 'dial()')();
    const phoneNumber: string = this.phoneNumber.value;
    if (!!phoneNumber) this.diallerSvc.callManual(phoneNumber, '', 30);
  }

  dialKeyDown(event: KeyboardEvent) {
    if (event.key == 'Enter') {
      this.dial();
      return false; // prevent the default action of the enter key
    }
    return true;
  }

  dtmfInput(event: Event) {
    const inputEvent = event as InputEvent;
    const val = inputEvent.data?.replace(/[^0-9*#]/g, '');
    if (!val) return false;
    this.sipSvc.sendDTMF(val);
    return true;
  }

  hangUp() {
    this.logger.info('DialPadComponent', 'hangup()')();
    // OK so if we have postCallSurveyData and it has not been cancelled
    // we need to transfer the call to the PCS server.
    // otherwise hangup.
    this.workSvc.postCallSurveyData$
      .pipe(
        take(1),
        withLatestFrom(
          this.workSvc.postCallSurveyCancel$,
          this.workSvc.postCallSurveyPhone$,
          this.workSvc.postCallSurveyCampaign$,
          this.workSvc.postCallSurveyRecordId$
        )
      )
      .subscribe(
        ([pcsData, pcsCancel, pcsPhone, pcsCampaignId, pcsRecordId]) => {
          if (pcsData !== undefined && !pcsCancel) {
            // we have a postCallSurvey to do.
            this.diallerSvc.postCallSurvey(
              pcsData,
              pcsPhone || '',
              pcsRecordId || 0,
              pcsCampaignId || 0
            );
          } else {
            this.diallerSvc.hangUp().subscribe();
          }
        }
      );
  }

  hold() {
    this.logger.info('DialPadComponent', 'hold()')();
    this.diallerSvc.onHold();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.dtmfMode$ = this.diallerSvc.state$.pipe(
      map((twState) => {
        switch (twState) {
          case TeleworkerState.CallDialling:
          case TeleworkerState.TransferIB:
          case TeleworkerState.TransferOB:
          case TeleworkerState.InboundConnected:
          case TeleworkerState.OutboundConnected:
            return true;
          default:
            return false;
        }
      }),
      shareReplayRefCount()
    );

    this.initButtonStates();
    this.initRemoteAudio();
    this.initFormGroup();
    this.initViewModel();
  }

  press(digit: NumPadDigit) {
    this.logger.info('DialPadComponent', 'press()', { digit })();
    combineLatest([this.dtmfMode$, this.sipSvc.voip$])
      .pipe(take(1))
      .subscribe(([dtmfMode, voip]) => {
        if (!dtmfMode) {
          this.phoneNumber.setValue(`${this.phoneNumber.value}${digit}`);
        }

        if (dtmfMode && voip) {
          // send down the SIP channel
          this.sipSvc.sendDTMF(`${digit}`);
          this.dtmfTones.setValue(`${this.dtmfTones.value}${digit}`, {
            emitEvent: false,
          });
        }
      });
  }

  speedDial() {
    this.logger.info('DialPadComponent', 'speedDial()')();
    this.modalService.open(SpeeddialComponent);
  }

  transfer() {
    this.logger.info('DialPadComponent', 'transfer()')();
    this.diallerSvc.getTransferAgents('');
    this.diallerSvc.transferAgents$?.pipe(take(1)).subscribe(({}) => {
      this.modalService.open(TransferComponent);
    });
  }

  unavailable() {
    this.logger.info('DialPadComponent', 'unavailable()')();
    this.diallerSvc.cancelGetNextNumber();
    this.workSvc.showStateModal();
  }

  unhold() {
    this.logger.info('DialPadComponent', 'unhold()')();
    this.diallerSvc.offHold();
  }
}

interface ViewModel {
  answer?: ButtonState;
  cancel?: ButtonState;
  dial?: ButtonState;
  dtmfMode?: boolean;
  hangUp?: ButtonState;
  hold?: ButtonState;
  numpad?: ButtonState;
  phoneNumber?: ButtonState;
  sipStatus?: { error: boolean; msg: string };
  speedDial?: ButtonState;
  transfer?: ButtonState;
  unavailable?: ButtonState;
  unhold?: ButtonState;
}
interface ButtonState {
  enabled: boolean;
  hoverText: string;
  visible: boolean;
}

type NumPadDigit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | '*' | '#';
